/**
 * QorTrace · PlaybookInboxBell
 *
 * Lightweight bell icon that surfaces the unread count of playbook
 * share-view notifications.  Click → small dropdown panel listing
 * the most recent rows.  Each row is a deep-link into the relevant
 * scan detail page (which is where the SharePanel lives).
 *
 * Polls every 60 s while the account layout is mounted.  Cheap call
 * (1 small GET on a tiny user-scoped collection).
 */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Bell, CheckCheck, Eye, Loader2, MessageSquarePlus, Check, ChevronDown, ChevronUp, Trash2, UserPlus, ExternalLink } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PlaybookInboxBell() {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [openCount, setOpenCount] = useState(0);
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [anchorPos, setAnchorPos] = useState({ left: 0, top: 0 });
  const [filter, setFilter] = useState("all"); // all | unread | open | followed_up
  const [expanded, setExpanded] = useState({});
  const popRef = useRef(null);
  const btnRef = useRef(null);

  const recomputeAnchor = () => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const desiredLeft = Math.max(8, r.right - 360);
    const top = r.bottom + 8;
    setAnchorPos({ left: desiredLeft, top });
  };

  const load = async (overrideFilter = null) => {
    const f = overrideFilter ?? filter;
    const params = new URLSearchParams({ limit: "15" });
    if (f === "unread")        params.set("only_unread", "true");
    else if (f === "open")     params.set("filter", "open");
    else if (f === "followed_up") params.set("filter", "followed_up");
    try {
      const { data } = await axios.get(
        `${API}/api/inventory/notifications?${params.toString()}`,
        { withCredentials: true },
      );
      setUnread(data.unread_count || 0);
      setOpenCount(data.open_count || 0);
      setItems(data.items || []);
    } catch (_e) { /* silent */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(() => load(), 60_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload when filter changes (only while open).
  useEffect(() => {
    if (open) load(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  // Close on outside-click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (popRef.current && popRef.current.contains(e.target)) return;
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Keep the panel anchored to the bell on scroll / resize so it doesn't drift.
  useEffect(() => {
    if (!open) return;
    recomputeAnchor();
    const handler = () => recomputeAnchor();
    window.addEventListener("scroll", handler, true);
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("scroll", handler, true);
      window.removeEventListener("resize", handler);
    };
  }, [open]);

  const markAllRead = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/inventory/notifications/mark-read`,
                       {}, { withCredentials: true });
      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button type="button"
              ref={btnRef}
              onClick={() => {
                setOpen((v) => !v);
                if (!open) { load(); recomputeAnchor(); }
              }}
              data-testid="playbook-inbox-bell"
              aria-label="Playbook view notifications"
              style={{
                position: "relative", padding: 6, borderRadius: 4,
                background: open ? "rgba(122,231,242,0.10)" : "transparent",
                border: `1px solid ${open ? "var(--qt-cyan)" : "transparent"}`,
                color: unread > 0 ? "var(--qt-cyan)" : "var(--qt-text-3)",
                transition: "all 120ms ease",
                cursor: "pointer",
              }}>
        <Bell size={15} />
        {unread > 0 && (
          <span data-testid="playbook-inbox-unread-badge"
                style={{
                  position: "absolute", top: -2, right: -2,
                  minWidth: 14, height: 14, padding: "0 3px",
                  background: "#FF6E6E", color: "#fff",
                  borderRadius: 7, fontSize: 9, fontWeight: 700,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  border: "1px solid var(--qt-bg)",
                }}>
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="playbook-inbox-panel" ref={popRef}
             style={{
               position: "fixed",
               left: anchorPos.left, top: anchorPos.top,
               width: 360, maxHeight: 480, overflow: "auto",
               background: "var(--qt-panel-bg, #0B141F)",
               border: "1px solid var(--qt-border)",
               borderRadius: 4, zIndex: 60,
               boxShadow: "0 12px 28px rgba(0,0,0,0.55)",
             }}>
          <div className="flex items-center justify-between px-3 py-2"
               style={{ borderBottom: "1px solid var(--qt-border)" }}>
            <div className="qt-label" style={{ color: "var(--qt-cyan)", margin: 0 }}>
              <Bell size={10} style={{ display: "inline", marginRight: 4 }} />
              PLAYBOOK VIEWS
            </div>
            {unread > 0 && (
              <button type="button" onClick={markAllRead} disabled={busy}
                      data-testid="playbook-inbox-mark-read"
                      className="qt-mono text-[10px]"
                      style={{
                        background: "transparent", border: "none",
                        color: "var(--qt-text-3)", letterSpacing: "0.10em",
                        cursor: "pointer", padding: 0,
                      }}>
                {busy ? <Loader2 size={9} className="animate-spin" /> : <CheckCheck size={9} />}
                {" "}MARK ALL READ
              </button>
            )}
          </div>

          {/* Filter chips */}
          <div className="flex items-center gap-1 px-3 py-2 flex-wrap"
               style={{ borderBottom: "1px solid var(--qt-border)",
                        background: "rgba(7,9,15,0.4)" }}>
            <FilterChip label="All" active={filter === "all"} onClick={() => setFilter("all")}
                        testid="playbook-inbox-filter-all" />
            <FilterChip label={`Unread${unread ? ` · ${unread}` : ""}`} active={filter === "unread"}
                        onClick={() => setFilter("unread")} accent={unread > 0 ? "#FF6E6E" : null}
                        testid="playbook-inbox-filter-unread" />
            <FilterChip label={`Open${openCount ? ` · ${openCount}` : ""}`} active={filter === "open"}
                        onClick={() => setFilter("open")} accent={openCount > 0 ? "#FFB000" : null}
                        testid="playbook-inbox-filter-open" />
            <FilterChip label="Followed up" active={filter === "followed_up"}
                        onClick={() => setFilter("followed_up")} accent="#1FDD83"
                        testid="playbook-inbox-filter-followed-up" />
          </div>

          {items == null ? (
            <div className="qt-mono text-[11px] px-3 py-4"
                 style={{ color: "var(--qt-text-3)" }}>
              Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="qt-mono text-[11px] px-3 py-4"
                 style={{ color: "var(--qt-text-3)" }}>
              {filter === "all"
                ? "No share views yet. Mint a playbook share to start tracking who opens it."
                : `No notifications match the "${filter.replace("_", " ")}" filter.`}
            </div>
          ) : (
            <ul className="m-0 p-0" style={{ listStyle: "none" }}>
              {items.map((n) => (
                <NotificationRow key={n.id} notif={n}
                                 expanded={!!expanded[n.id]}
                                 onToggleExpand={() => setExpanded((p) => ({ ...p, [n.id]: !p[n.id] }))}
                                 onChanged={() => load()}
                                 onNavigate={() => setOpen(false)} />
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  );
}


function FilterChip({ label, active, onClick, accent, testid }) {
  return (
    <button type="button" onClick={onClick}
            data-testid={testid}
            className="qt-mono text-[10px] px-2 py-1 rounded transition-colors"
            style={{
              letterSpacing: "0.10em",
              border: `1px solid ${active ? (accent || "var(--qt-cyan)") : "var(--qt-border)"}`,
              background: active ? `${accent || "var(--qt-cyan)"}15` : "transparent",
              color: active ? (accent || "var(--qt-cyan)") : "var(--qt-text-3)",
              fontWeight: active ? 700 : 500, cursor: "pointer",
            }}>
      {label.toUpperCase()}
    </button>
  );
}


function NotificationRow({ notif, expanded, onToggleExpand, onChanged, onNavigate }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const annCount = (notif.annotations || []).length;
  const isFollowed = !!notif.is_followed_up;

  const annotate = async (e) => {
    e?.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/api/inventory/notifications/${encodeURIComponent(notif.id)}/annotate`,
        { text: text.trim(), mark_followed_up: true },
        { withCredentials: true },
      );
      setText("");
      onChanged();
    } finally { setBusy(false); }
  };

  const toggleFollowup = async () => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/api/inventory/notifications/${encodeURIComponent(notif.id)}/toggle-followup`,
        { value: !isFollowed }, { withCredentials: true },
      );
      onChanged();
    } finally { setBusy(false); }
  };

  const deleteAnnotation = async (idx) => {
    setBusy(true);
    try {
      await axios.delete(
        `${API}/api/inventory/notifications/${encodeURIComponent(notif.id)}/annotation/${idx}`,
        { withCredentials: true },
      );
      onChanged();
    } finally { setBusy(false); }
  };

  return (
    <li data-testid={`playbook-inbox-item-${notif.id}`}
        style={{
          borderBottom: "1px solid var(--qt-border)",
          background: notif.read ? "transparent" : "rgba(122,231,242,0.05)",
        }}>
      <div className="flex items-start gap-2 px-3 py-2.5">
        <Eye size={11}
             style={{ marginTop: 3,
                      color: notif.read ? "var(--qt-text-3)" : "var(--qt-cyan)" }} />
        <Link to={`/account/inventory?view=detail&id=${encodeURIComponent(notif.scan_id)}`}
              onClick={onNavigate}
              style={{ flex: 1, minWidth: 0, textDecoration: "none", color: "inherit" }}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[12.5px]"
                  style={{ color: "#fff", fontWeight: notif.read ? 500 : 700 }}>
              {notif.headline}
            </span>
            {isFollowed && (
              <span className="qt-mono text-[9px] px-1.5 py-0.5 rounded"
                    style={{ background: "rgba(31,221,131,0.10)",
                             border: "1px solid rgba(31,221,131,0.40)",
                             color: "#1FDD83", letterSpacing: "0.10em", fontWeight: 700 }}>
                FOLLOWED UP
              </span>
            )}
          </div>
          <div className="text-[11px] mt-0.5"
               style={{ color: "var(--qt-text-2)" }}>
            {notif.target_descriptor} · {notif.view_count}{notif.max_views ? `/${notif.max_views}` : ""} views
          </div>
          <div className="qt-mono text-[9.5px] mt-1"
               style={{ color: "var(--qt-text-3)", letterSpacing: "0.06em" }}>
            {new Date(notif.created_at).toLocaleString()}
          </div>
        </Link>
        <button type="button" onClick={onToggleExpand}
                data-testid={`playbook-inbox-expand-${notif.id}`}
                aria-label="Toggle annotations"
                style={{ background: "transparent", border: "none",
                         color: annCount > 0 ? "var(--qt-cyan)" : "var(--qt-text-3)",
                         cursor: "pointer", padding: 2, lineHeight: 1 }}>
          <span className="qt-mono text-[10px]" style={{ marginRight: 4 }}>
            {annCount > 0 ? annCount : ""}
          </span>
          {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </button>
      </div>

      {expanded && (
        <div data-testid={`playbook-inbox-annotations-${notif.id}`}
             style={{ padding: "0 12px 12px 28px",
                      borderTop: "1px dashed var(--qt-border)" }}>
          {(notif.annotations || []).length > 0 && (
            <ul className="m-0 p-0 mb-2 space-y-1.5" style={{ listStyle: "none", paddingTop: 8 }}>
              {(notif.annotations || []).map((a, i) => (
                <li key={i} data-testid={`playbook-inbox-annotation-${notif.id}-${i}`}
                    className="flex items-start gap-2"
                    style={{ padding: "6px 8px", borderRadius: 3,
                             background: "rgba(7,9,15,0.6)",
                             border: "1px solid var(--qt-border)" }}>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11.5px]" style={{ color: "#fff" }}>
                      {a.text}
                    </div>
                    <div className="qt-mono text-[9px] mt-1"
                         style={{ color: "var(--qt-text-3)", letterSpacing: "0.06em" }}>
                      {a.author_email} · {new Date(a.created_at).toLocaleString()}
                    </div>
                  </div>
                  <button type="button" onClick={() => deleteAnnotation(i)} disabled={busy}
                          data-testid={`playbook-inbox-delete-annotation-${notif.id}-${i}`}
                          aria-label="Delete annotation"
                          style={{ background: "transparent", border: "none",
                                   color: "var(--qt-text-3)", cursor: "pointer", padding: 2 }}>
                    <Trash2 size={10} />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <form onSubmit={annotate} className="flex items-stretch gap-1.5">
            <input type="text" value={text}
                   onChange={(e) => setText(e.target.value)}
                   placeholder="Add a follow-up note…"
                   data-testid={`playbook-inbox-annotation-input-${notif.id}`}
                   maxLength={600}
                   className="qt-input flex-1"
                   style={{ fontSize: 11.5, padding: "5px 8px" }} />
            <button type="submit" disabled={busy || !text.trim()}
                    data-testid={`playbook-inbox-annotation-submit-${notif.id}`}
                    className="qt-btn qt-btn-primary"
                    style={{ fontSize: 10.5, padding: "5px 9px" }}
                    title="Save note (also marks as followed up)">
              {busy ? <Loader2 size={10} className="animate-spin" /> : <MessageSquarePlus size={10} />}
            </button>
          </form>

          <button type="button" onClick={toggleFollowup} disabled={busy}
                  data-testid={`playbook-inbox-toggle-followup-${notif.id}`}
                  className="qt-mono text-[10px] mt-2 px-2 py-1 rounded"
                  style={{
                    letterSpacing: "0.10em",
                    background: "transparent",
                    border: `1px solid ${isFollowed ? "var(--qt-border)" : "rgba(31,221,131,0.40)"}`,
                    color: isFollowed ? "var(--qt-text-3)" : "#1FDD83",
                    cursor: "pointer", fontWeight: 600,
                  }}>
            {isFollowed
              ? <><CheckCheck size={9} style={{ display: "inline", marginRight: 4 }} />REOPEN</>
              : <><Check size={9} style={{ display: "inline", marginRight: 4 }} />MARK FOLLOWED UP</>}
          </button>

          {/* Convert hot-lead → CRM contact (iter 3.4) */}
          <ConvertToContactRow notif={notif} onChanged={onChanged} onNavigate={onNavigate} />
        </div>
      )}
    </li>
  );
}


function ConvertToContactRow({ notif, onChanged, onNavigate }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState(null);
  const hasContact = !!notif.contact_id;
  const canConvert = !!(notif.recipient_email || notif.recipient_label);

  const convert = async () => {
    if (!canConvert || busy) return;
    setBusy(true); setErr(null);
    try {
      const { data } = await axios.post(
        `${API}/api/contacts/from-notification/${encodeURIComponent(notif.id)}`,
        {}, { withCredentials: true },
      );
      onChanged();
      // Soft-redirect to the new contact page so the AE can keep selling.
      if (data?.contact?.id) {
        window.location.assign(`/account/contacts?id=${encodeURIComponent(data.contact.id)}`);
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Conversion failed.");
    } finally { setBusy(false); }
  };

  if (hasContact) {
    return (
      <div className="mt-2" data-testid={`playbook-inbox-contact-link-${notif.id}`}>
        <Link to={`/account/contacts?id=${encodeURIComponent(notif.contact_id)}`}
              onClick={onNavigate}
              className="qt-mono text-[10px] px-2 py-1 rounded inline-flex items-center"
              style={{
                letterSpacing: "0.10em",
                background: "rgba(122,231,242,0.08)",
                border: "1px solid var(--qt-cyan)",
                color: "var(--qt-cyan)", textDecoration: "none",
                fontWeight: 700,
              }}>
          <ExternalLink size={9} style={{ marginRight: 4 }} />
          VIEW CONTACT
        </Link>
      </div>
    );
  }

  return (
    <div className="mt-2">
      <button type="button" onClick={convert} disabled={!canConvert || busy}
              data-testid={`playbook-inbox-convert-contact-${notif.id}`}
              className="qt-mono text-[10px] px-2 py-1 rounded inline-flex items-center"
              title={canConvert
                ? "Create a CRM contact from this lead — copies the share view + all annotations as the first activity"
                : "Mint a share with a recipient label or email to enable lead capture"}
              style={{
                letterSpacing: "0.10em",
                background: "transparent",
                border: `1px solid ${canConvert ? "rgba(255,176,0,0.50)" : "var(--qt-border)"}`,
                color: canConvert ? "#FFB000" : "var(--qt-text-3)",
                cursor: canConvert ? "pointer" : "not-allowed",
                fontWeight: 600, opacity: busy ? 0.6 : 1,
              }}>
        {busy
          ? <Loader2 size={9} className="animate-spin" style={{ marginRight: 4 }} />
          : <UserPlus size={9} style={{ marginRight: 4 }} />}
        CONVERT TO CONTACT
      </button>
      {err && (
        <div className="qt-mono text-[9.5px] mt-1"
             data-testid={`playbook-inbox-convert-error-${notif.id}`}
             style={{ color: "#FF6E6E", letterSpacing: "0.04em" }}>
          {err}
        </div>
      )}
    </div>
  );
}
